/**
 * Phasen-Zeitleiste eines Einsatzes (gesetzt von der Führungskraft) und die zugehörigen Checklisten.
 * Typ EINSATZ und UEBUNGSEINSATZ: Phase = Einsatzstatus. Typ UEBUNG (reine Übung): eigene Phasenleiste mit 5 Phasen und Übungstexten;
 * die Phase steht in `einsaetze.phase` (Schlüssel wie bei Einsätzen, ABFAHRT_WACHE gibt es nicht und gilt als Übungsbereitschaft),
 * der Status bleibt GEPLANT/LAUFEND/BEENDET/ABGESCHLOSSEN und wird vom Server mit der Phase gekoppelt.
 * Die Schlüssel (ALARMIERUNG …, EINSATZBEREITSCHAFT …) bleiben in DB/API; hier stehen nur Anzeige-Texte.
 */
export const EINSATZ_PHASEN_ZEITLEISTE = [
  { key: 'ALARMIERUNG', label: 'Alarmierung' },
  { key: 'ABFAHRT_WACHE', label: 'Abfahrt Wache' },
  { key: 'ANKUNFT_EINSATZORT', label: 'Ankunft Einsatzort' },
  { key: 'EINSATZENDE', label: 'Einsatzende' },
  { key: 'EINSATZBEREIT', label: 'Einsatzbereitschaft wiederhergestellt' },
] as const

/** Typ UEBUNG: eigene Texte, ohne Abfahrt Wache; dieselben Schlüssel wie bei Einsätzen (Checklisten, Drohnenfreigabe) */
export const UEBUNG_PHASEN = [
  { key: 'ALARMIERUNG', label: 'Übungsbereitschaft' },
  { key: 'ANKUNFT_EINSATZORT', label: 'Ankunft am Übungsort' },
  { key: 'EINSATZENDE', label: 'Übungsende' },
  { key: 'EINSATZBEREIT', label: 'Einsatzbereitschaft wiederhergestellt' },
  { key: 'ABGESCHLOSSEN', label: 'Abgeschlossen' },
] as const

export const PHASE_GEPLANT = { key: 'GEPLANT', label: 'Geplant' } as const
export const PHASE_ABGESCHLOSSEN = { key: 'ABGESCHLOSSEN', label: 'Abgeschlossen' } as const

export type EinsatzPhaseKey = typeof EINSATZ_PHASEN_ZEITLEISTE[number]['key'] | 'GEPLANT' | 'ABGESCHLOSSEN'

/** EINSATZ: 5 Phasen + „Abgeschlossen“ (von Hand); UEBUNGSEINSATZ zusätzlich „Geplant“ ganz vorn (Status = Phase); UEBUNG: 5 Übungsphasen (Spalte phase) */
export const phasenFuerTyp = (typ?: string | null): readonly { key: EinsatzPhaseKey; label: string }[] =>
  typ === 'UEBUNG' ? UEBUNG_PHASEN
    : typ === 'UEBUNGSEINSATZ' ? [PHASE_GEPLANT, ...EINSATZ_PHASEN_ZEITLEISTE, PHASE_ABGESCHLOSSEN]
    : typ === 'EINSATZ' ? [...EINSATZ_PHASEN_ZEITLEISTE, PHASE_ABGESCHLOSSEN] : EINSATZ_PHASEN_ZEITLEISTE

export const phaseIndex = (k?: string | null, typ?: string | null) => phasenFuerTyp(typ).findIndex(p => p.key === k)
/** Anzeige-Text einer Phase; mit typ = UEBUNG die Übungstexte („Übungsbereitschaft“ …) */
export const phaseLabel = (k?: string | null, typ?: string | null) =>
  (typ === 'UEBUNG' ? UEBUNG_PHASEN : [PHASE_GEPLANT, ...EINSATZ_PHASEN_ZEITLEISTE, PHASE_ABGESCHLOSSEN]).find(p => p.key === k)?.label ?? ''

/** Hat der Einsatz eine Phasenleiste? EINSATZ, UEBUNGSEINSATZ und UEBUNG. */
export const hatPhasenleiste = (typ?: string | null) => typ === 'EINSATZ' || typ === 'UEBUNGSEINSATZ' || typ === 'UEBUNG'

/** Aktuelle Phase: EINSATZ/UEBUNGSEINSATZ -> Status; UEBUNG -> Spalte phase (ABFAHRT_WACHE älterer Übungen = Übungsbereitschaft,
 *  Status ABGESCHLOSSEN = Abgeschlossen); null = noch keine Phase gesetzt */
export function aktuellePhase(e?: { typ?: string | null; status?: string | null; phase?: string | null } | null): EinsatzPhaseKey | null {
  if (!e || !hatPhasenleiste(e.typ)) return null
  if (e.typ === 'UEBUNG') {
    if (String(e.status ?? '').toUpperCase() === 'ABGESCHLOSSEN') return 'ABGESCHLOSSEN'
    let p = String(e.phase ?? '').toUpperCase()
    if (p === 'ABFAHRT_WACHE') p = 'ALARMIERUNG'
    return phaseIndex(p, e.typ) >= 0 ? (p as EinsatzPhaseKey) : null
  }
  const roh = String(e.status ?? '').toUpperCase()
  return phaseIndex(roh, e.typ) >= 0 ? (roh as EinsatzPhaseKey) : null
}

/** Bei Typ UEBUNG heißt es „Übungsbereitschaft“, sonst „Einsatzbereitschaft“ (nur Anzeige) */
export const bereitschaftWort = (typ?: string | null) => (typ === 'UEBUNG' ? 'Übungsbereitschaft' : 'Einsatzbereitschaft')

/** Checklisten je Phase der Zeitleiste (Einsatz): Modul + Checklisten-Phase */
export const PHASEN_CHECKLISTEN: Record<EinsatzPhaseKey, { modul: 'FUEHRUNG' | 'PILOT'; phase: string }[]> = {
  ALARMIERUNG: [{ modul: 'FUEHRUNG', phase: 'EINSATZBEREITSCHAFT' }, { modul: 'PILOT', phase: 'EINSATZBEREITSCHAFT' }],
  ABFAHRT_WACHE: [{ modul: 'FUEHRUNG', phase: 'ANFAHRT_BRIEFING' }],
  ANKUNFT_EINSATZORT: [{ modul: 'PILOT', phase: 'EINSATZORT' }],
  EINSATZENDE: [{ modul: 'FUEHRUNG', phase: 'EINSATZENDE' }, { modul: 'PILOT', phase: 'EINSATZENDE_PILOT' }],
  EINSATZBEREIT: [],
  GEPLANT: [],
  ABGESCHLOSSEN: [],
}

/** Typ UEBUNG: ohne Abfahrt Wache gehört „Anfahrt & Briefing“ (Führung) mit in die erste Phase „Übungsbereitschaft“ */
export const UEBUNG_PHASEN_CHECKLISTEN: Record<EinsatzPhaseKey, { modul: 'FUEHRUNG' | 'PILOT'; phase: string }[]> = {
  ...PHASEN_CHECKLISTEN,
  ALARMIERUNG: [{ modul: 'FUEHRUNG', phase: 'EINSATZBEREITSCHAFT' }, { modul: 'FUEHRUNG', phase: 'ANFAHRT_BRIEFING' }, { modul: 'PILOT', phase: 'EINSATZBEREITSCHAFT' }],
  ABFAHRT_WACHE: [],
}

/** Zeitleisten-Phase, zu der eine Einsatz-Checkliste gehört (null = flug-gebunden / unbekannt) */
export function phaseDerChecklist(modul: string, phase: string, typ?: string | null): EinsatzPhaseKey | null {
  const tabelle = typ === 'UEBUNG' ? UEBUNG_PHASEN_CHECKLISTEN : PHASEN_CHECKLISTEN
  for (const p of EINSATZ_PHASEN_ZEITLEISTE) {
    if (tabelle[p.key].some(c => c.modul === modul && c.phase === phase)) return p.key
  }
  return null
}

/** Anzeigename einer Checkliste (typabhängig) */
export function checklistenAnzeigeName(phase: string, typ?: string | null): string | null {
  switch (phase) {
    case 'EINSATZBEREITSCHAFT': return bereitschaftWort(typ)
    case 'EINSATZORT': return typ === 'UEBUNG' ? 'Übungsbereitschaft am Übungsort' : 'Einsatzbereitschaft am Einsatzort'
    case 'ANFAHRT_BRIEFING': return 'Anfahrt & Briefing'
    case 'EINSATZENDE': case 'EINSATZENDE_PILOT': return typ === 'UEBUNG' ? 'Übungsende' : 'Einsatzende'
    default: return null
  }
}

export interface ChecklistenStandInfo { id: number; modul: string; phase: string; flug_id?: number | null; status?: string; items: { pflicht: boolean; wert: string | null }[] }

/** Anzahl erledigter / gesamter Pflichtpunkte; fertig = alle Pflichtpunkte erledigt (oder Checkliste abgeschlossen) */
export function checklistenFortschritt(i: ChecklistenStandInfo) {
  const pflicht = i.items.filter(x => x.pflicht)
  const erledigt = pflicht.filter(x => x.wert && x.wert !== '').length
  return { erledigt, gesamt: pflicht.length, offen: pflicht.length - erledigt, fertig: i.status === 'ABGESCHLOSSEN' || erledigt >= pflicht.length }
}

/** Offene Einsatz-Checklisten (nur die der Phasen-Zuordnung bei Führung und Pilot; Flug-Checklisten zählen nicht).
 *  bisPhase: nur Phasen bis einschließlich dieser Phase; ohnePhase: Checklisten dieser Phase auslassen. */
export function offeneEinsatzChecklisten<T extends ChecklistenStandInfo & { name: string }>(
  liste: T[], opt: { typ?: string | null; bisPhase?: EinsatzPhaseKey | null; ohnePhase?: EinsatzPhaseKey | null } = {},
) {
  const gi = opt.bisPhase ? EINSATZ_PHASEN_ZEITLEISTE.findIndex(p => p.key === opt.bisPhase) : -1
  const grenze = gi >= 0 ? gi : EINSATZ_PHASEN_ZEITLEISTE.length - 1
  const aus: { instanz: T; phase: EinsatzPhaseKey; offen: number; text: string }[] = []
  for (const c of liste) {
    if (c.flug_id) continue
    const pk = phaseDerChecklist(c.modul, c.phase, opt.typ)
    if (!pk || pk === opt.ohnePhase) continue
    if (EINSATZ_PHASEN_ZEITLEISTE.findIndex(p => p.key === pk) > grenze) continue
    const f = checklistenFortschritt(c)
    if (f.fertig) continue
    aus.push({ instanz: c, phase: pk, offen: f.offen, text: `${c.modul === 'PILOT' ? 'Pilot' : 'Führung'}: ${checklistenAnzeigeName(c.phase, opt.typ) ?? c.name} (${f.offen} offen)` })
  }
  return aus.sort((a, b) => EINSATZ_PHASEN_ZEITLEISTE.findIndex(p => p.key === a.phase) - EINSATZ_PHASEN_ZEITLEISTE.findIndex(p => p.key === b.phase))
}

/** Drohnen-Zuordnung: „aktiv gebunden“, solange der Einsatz nicht bereit ist. Freigabe ab EINSATZBEREIT
 *  (EINSATZ/UEBUNGSEINSATZ: Status EINSATZBEREIT oder ABGESCHLOSSEN; UEBUNG: Phase EINSATZBEREIT oder Status BEENDET/ABGESCHLOSSEN). */
export function drohnenFreigegeben(e?: { typ?: string | null; status?: string | null; phase?: string | null } | null): boolean {
  if (!e) return false
  const status = String(e.status ?? '').toUpperCase()
  if (status === 'ABGESCHLOSSEN' || status === 'BEENDET') return true
  if (e.typ === 'UEBUNG') return String(e.phase ?? '').toUpperCase() === 'EINSATZBEREIT'   // bestehende Übungen mit früher gesetzter Phase
  return status === 'EINSATZBEREIT'
}
